import { Title } from '@sr-sdks/api-sdk-axios';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { queryOptionsGetTitlesPaginated } from '../api/titles/titlesApi';
import { useActiveWorkspace } from '../hooks/useActiveWorkspace';
import { useOwnerClientNames } from '../hooks/useClientNames';
import { cappedPagination } from '../utils/helpers/cappedTotal';
import { workspaceTypeIsAgent } from '../utils/helpers/common';
import { EntityList } from './EntityList';
import { MSR3Empty } from './MSR3Empty';
import { MultipleQueryResults } from './QueryResults/MultipleQueryResults';
import { SnapshotListSkeleton } from './SnapshotListSkeleton';
import { SnapshotViewAll } from './SnapshotViewAll';
import { TitleCard, TitleCardSkeleton } from './TitleCard/TitleCard';

/**
 * How many titles the Home "Titles" section shows before the rest are only
 * reachable on the Titles page. Also sets the skeleton card count and the
 * "View All" threshold, so they can't drift apart.
 */
export const HOME_VISIBLE_TITLES = 5;

interface TitlesSnapshotContentProps {
  basePath: string;
  isAgent: boolean;
  /** `totalTitles` is a floor: the list stopped counting at its limit. */
  isTotalCapped?: boolean;
  titles: Title[];
  totalTitles: number;
  workspaceId?: string;
}

const TitlesSnapshotContent = ({
  basePath,
  isAgent,
  isTotalCapped,
  titles,
  totalTitles,
  workspaceId,
}: TitlesSnapshotContentProps) => {
  const pageClients = useOwnerClientNames(titles, {
    enabled: isAgent,
    workspaceId,
  });

  return (
    <EntityList>
      <EntityList.Header>
        <EntityList.Title>Titles</EntityList.Title>
        <EntityList.Count count={totalTitles} isCapped={isTotalCapped} />

        {workspaceId && (
          <SnapshotViewAll
            to={`/workspace/${workspaceId}/titles`}
            total={totalTitles}
            visibleCount={HOME_VISIBLE_TITLES}
          />
        )}
      </EntityList.Header>

      {titles.length === 0 ? (
        <EntityList.Empty {...MSR3Empty.Props.title} />
      ) : (
        <EntityList.Content>
          {titles.map((title) => (
            <TitleCard
              key={title._id}
              title={title}
              basePath={basePath}
              clientNames={pageClients.getClientNamesOrFallback(
                title.ownerClientIds,
              )}
              isClientNamesLoading={pageClients.isLoading}
            />
          ))}
        </EntityList.Content>
      )}
    </EntityList>
  );
};

/**
 * Home "Titles" snapshot — the first `HOME_VISIBLE_TITLES` titles, A–Z by
 * name, with a "View All" link to the full Titles page. Reuses the
 * same `TitleCard` as that page so the two views match.
 */
export const TitlesSnapshot = () => {
  const { workspaceId } = useParams();
  // The workspace query is the real gate here, not `useParams`: `workspaceId`
  // resolves synchronously from the route, but `activeWorkspaceQuery` is
  // async. Reading only the tuple's first element (as before) and deriving
  // `isAgent` from it meant the titles query — and the client-name query it
  // gates via `isAgent` — started before the workspace was known, so cards
  // painted as clientless and then reflowed once it resolved. Gating on both
  // queries together (below) holds the whole section until the workspace
  // resolves, without an early return above these hooks.
  const [, activeWorkspaceQuery] = useActiveWorkspace();
  const basePath = `/workspace/${workspaceId ?? ''}`;

  const titlesQuery = useQuery(
    queryOptionsGetTitlesPaginated({
      // Series level only, same as TitlesV3 — despite the name, this keeps
      // one-off titles and only excludes season/episode rows.
      seriesOnly: true,
      workspaceId,
    }),
  );

  return (
    <MultipleQueryResults
      loadingFallback={
        <SnapshotListSkeleton
          label="Loading titles"
          hasTitle
          numCards={HOME_VISIBLE_TITLES}
          renderCard={() => <TitleCardSkeleton />}
        />
      }
      queries={{ titles: titlesQuery, workspace: activeWorkspaceQuery }}
    >
      {({ titles: titlesResponse, workspace }) => {
        const { isTotalCapped, totalItems } = cappedPagination(
          titlesResponse?.meta,
        );

        return (
          <TitlesSnapshotContent
            basePath={basePath}
            isAgent={workspaceTypeIsAgent(workspace)}
            isTotalCapped={isTotalCapped}
            titles={((titlesResponse?.items as Title[]) || []).slice(
              0,
              HOME_VISIBLE_TITLES,
            )}
            totalTitles={totalItems}
            workspaceId={workspaceId}
          />
        );
      }}
    </MultipleQueryResults>
  );
};
